import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { formatTime } from '@/lib/format';
import { useUserId } from '@/components/AuthProvider';
import { PageHeader } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './AdminChat.module.css';

type Message = {
  id: string;
  sender_id: string;
  message: string;
  created_at: string;
};

export default function AdminChat() {
  const { userId: targetUserId } = useParams<{ userId: string }>();
  const adminId = useUserId();

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [userName, setUserName] = useState('Utilisateur');
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const transcriptRef = useRef<HTMLDivElement>(null);

  const loadMessages = useCallback(async (conversation: string) => {
    const { data } = await supabase
      .from('admin_messages')
      .select('id, sender_id, message, created_at')
      .eq('conversation_id', conversation)
      .order('created_at', { ascending: true });
    if (data) setMessages(data as Message[]);
  }, []);

  useEffect(() => {
    if (!adminId || !targetUserId) return;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let active = true;

    void (async () => {
      setLoading(true);

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', targetUserId)
        .maybeSingle();
      if (active && profile?.full_name) setUserName(profile.full_name);

      // Get or create the private conversation with this user.
      let conversation: string | null = null;
      const { data: existing } = await supabase
        .from('admin_conversations')
        .select('id')
        .eq('admin_id', adminId)
        .eq('user_id', targetUserId)
        .maybeSingle();

      if (existing) {
        conversation = existing.id;
      } else {
        const { data: created, error } = await supabase
          .from('admin_conversations')
          .insert({ admin_id: adminId, user_id: targetUserId })
          .select('id')
          .single();
        if (error || !created) {
          if (active) setLoading(false);
          return;
        }
        conversation = created.id;
      }

      if (!active || !conversation) return;

      setConversationId(conversation);
      await loadMessages(conversation);
      setLoading(false);

      channel = supabase
        .channel(`admin_conv_${conversation}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'admin_messages',
            filter: `conversation_id=eq.${conversation}`,
          },
          (payload) => {
            const incoming = payload.new as Message;
            setMessages((current) =>
              current.some((item) => item.id === incoming.id) ? current : [...current, incoming]
            );
          }
        )
        .subscribe();
    })();

    return () => {
      active = false;
      void channel?.unsubscribe();
    };
  }, [adminId, targetUserId, loadMessages]);

  useEffect(() => {
    const node = transcriptRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  const sendMessage = async () => {
    const trimmed = text.trim();
    if (!trimmed || !conversationId || !adminId || sending) return;

    setSending(true);
    setText('');
    const { error } = await supabase.from('admin_messages').insert({
      conversation_id: conversationId,
      sender_id: adminId,
      message: trimmed,
    });
    if (error) setText(trimmed); // restore the draft on failure
    setSending(false);
  };

  return (
    <div className={styles.page}>
      <PageHeader title={userName} subtitle="Conversation privée" back="/admin" />

      {loading ? (
        <Spinner block />
      ) : (
        <div className={styles.transcript} ref={transcriptRef}>
          {messages.length === 0 ? (
            <p className={styles.empty}>Commencez la conversation</p>
          ) : (
            messages.map((message) => {
              const mine = message.sender_id === adminId;
              return (
                <div
                  key={message.id}
                  className={`${styles.bubble} ${mine ? styles.bubbleAdmin : styles.bubbleUser}`}
                >
                  <p className={styles.text}>{message.message}</p>
                  <span className={styles.time}>{formatTime(message.created_at)}</span>
                </div>
              );
            })
          )}
        </div>
      )}

      <div className={styles.composer}>
        <textarea
          className={styles.input}
          placeholder="Écrire un message..."
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void sendMessage();
            }
          }}
          maxLength={500}
          rows={1}
          aria-label="Message"
        />
        <button
          type="button"
          className={styles.sendBtn}
          onClick={() => void sendMessage()}
          disabled={!text.trim() || sending}
          aria-label="Envoyer"
        >
          {sending ? <Spinner size={16} color="#fff" /> : <Send size={18} />}
        </button>
      </div>
    </div>
  );
}
